import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  ArrowLeftIcon,
  PlayIcon,
  PauseIcon,
  CheckIcon,
  ClockIcon,
  TruckIcon,
  UserIcon,
  CalendarIcon,
  PhotoIcon,
  XMarkIcon,
  MagnifyingGlassPlusIcon
} from '@heroicons/react/24/outline';
import { formatDistanceToNow, getPriorityColor, getStatusColor, formatDuration, cn } from '../utils';
import apiService from '../services/api';
import toast from 'react-hot-toast';

// Helper function to construct proper image URLs for web
const getImageUrl = (attachment) => {
  if (!attachment || attachment.type !== 'image') return null;
  
  // Prioritize URL property (S3 or full URL)
  if (attachment.url) {
    return attachment.url;
  }
  
  // Handle path property
  if (attachment.path) {
    // If path starts with http/https, it's already a full URL (S3)
    if (attachment.path.startsWith('http')) {
      return attachment.path;
    }
    
    // If local path, construct full URL
    const SERVER_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5300';
    if (attachment.path.startsWith('uploads/')) {
      return `${SERVER_BASE_URL}/${attachment.path}`;
    }
    
    // For other local paths, ensure proper construction
    return `${SERVER_BASE_URL}/uploads/${attachment.path.replace(/^\/+/, '')}`;
  }
  
  return null;
};

const TaskDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionNotes, setCompletionNotes] = useState('');

  useEffect(() => {
    if (id) {
      fetchTask();
    }
  }, [id]);

  const fetchTask = async () => {
    try {
      setLoading(true);
      const response = await apiService.getTask(id);
      if (response.success && response.task) {
        setTask(response.task);
      } else {
        toast.error('Task not found');
        navigate('/tasks');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to load task');
      navigate('/tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleTaskAction = async (action) => {
    if (!task) return;
    
    try {
      setActionLoading(true);
      let response;
      
      switch (action) {
        case 'start':
          response = await apiService.startTask(task._id);
          break;
        case 'pause':
          response = await apiService.pauseTask(task._id);
          break;
        case 'complete':
          response = await apiService.completeTask(task._id, completionNotes);
          setShowCompletionModal(false);
          setCompletionNotes('');
          break;
        default:
          return;
      }
      
      if (response.success) {
        toast.success(`Task ${action}ed successfully`);
        fetchTask(); // Refresh task data
      }
    } catch (error) {
      toast.error(error.message || `Failed to ${action} task`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };

  const canManageTask = user?.role === 'admin' || task?.assignedTo?._id === user?.id;

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-white">Loading task...</p>
        </div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <div className="text-center">
          <ClockIcon className="h-12 w-12 text-dark-400 mx-auto mb-4" />
          <p className="text-white">Task not found</p>
        </div>
      </div>
    );
  }

  const taskImages = task.attachments ? task.attachments.filter(att => att.type === 'image') : [];

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Header */}
      <div className="bg-dark-800 border-b border-dark-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/tasks')}
                className="mr-4 p-2 text-dark-400 hover:text-white transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
              <h1 className="text-xl font-semibold text-white">Task Details</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-dark-800 border border-dark-700 rounded-lg p-6">
          {/* Task Header */}
          <div className="mb-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-white mb-3">{task.title}</h2>
                <div className="flex items-center space-x-3">
                  <span className={cn('badge', getPriorityColor(task.priority))}>
                    {task.priority}
                  </span>
                  <span className={cn('badge', getStatusColor(task.status))}>
                    {task.status}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          {task.description && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-white mb-2">Description</h3>
              <p className="text-dark-300">{task.description}</p>
            </div>
          )}

          {/* Assignment Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="text-lg font-medium text-white mb-3">Assignment</h3>
              <div className="space-y-2">
                <div className="flex items-center text-dark-300">
                  <UserIcon className="h-4 w-4 mr-2" />
                  <span>Assigned to: {task.assignedTo?.name}</span>
                </div>
                <div className="flex items-center text-dark-300">
                  <UserIcon className="h-4 w-4 mr-2" />
                  <span>Assigned by: {task.assignedBy?.name}</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-3">Timeline</h3>
              <div className="space-y-2">
                <div className="flex items-center text-dark-300">
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  <span>Created: {formatDistanceToNow(task.createdAt)}</span>
                </div>
                {task.dueDate && (
                  <div className="flex items-center text-dark-300">
                    <ClockIcon className="h-4 w-4 mr-2" />
                    <span>Due: {formatDistanceToNow(task.dueDate)}</span>
                  </div>
                )}
                {task.completedAt && (
                  <div className="flex items-center text-green-400">
                    <CheckIcon className="h-4 w-4 mr-2" />
                    <span>Completed: {formatDistanceToNow(task.completedAt)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Car Info */}
          {task.car && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-white mb-3">Vehicle</h3>
              <div className="bg-dark-700 rounded-lg p-4">
                <div className="flex items-center">
                  <TruckIcon className="h-5 w-5 text-dark-400 mr-3" />
                  <div>
                    <p className="text-white font-medium">{task.car.numberPlate}</p>
                    <p className="text-dark-300">
                      {task.car.year} {task.car.make} {task.car.model}
                    </p>
                    {task.car.owner && (
                      <p className="text-sm text-dark-400">Owner: {task.car.owner.name}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Duration Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="text-lg font-medium text-white mb-2">Duration</h3>
              <div className="space-y-1 text-dark-300">
                <p>Estimated: {formatDuration(task.estimatedDuration || 0)}</p>
                <p>Actual: {formatDuration(task.actualDuration || 0)}</p>
              </div>
            </div>

            {/* Time Entries */}
            {task.timeEntries && task.timeEntries.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-white mb-2">Time Entries</h3>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {task.timeEntries.map((entry, index) => (
                    <div key={index} className="bg-dark-700 p-2 rounded text-sm">
                      <p className="text-dark-300">
                        {new Date(entry.startTime).toLocaleString()}
                        {entry.endTime && ` - ${new Date(entry.endTime).toLocaleString()}`}
                      </p>
                      <p className="text-white font-medium">
                        Duration: {formatDuration(entry.duration || 0)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Task Images */}
          {taskImages.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-white mb-3">Task Images</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {taskImages.map((image, index) => {
                  const imageUrl = getImageUrl(image);
                  
                  if (!imageUrl) {
                    console.warn('No valid image URL found for attachment:', image);
                    return null;
                  }
                  
                  return (
                    <div key={index} className="relative group">
                      <div 
                        className="aspect-square bg-dark-700 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary-500 transition-all"
                        onClick={() => handleImageClick(imageUrl)}
                      >
                        <img
                          src={imageUrl}
                          alt={image.originalName || `Task image ${index + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            console.error('Failed to load image:', imageUrl);
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                          onLoad={() => {
                            console.log('Image loaded successfully:', imageUrl);
                          }}
                        />
                        <div className="hidden w-full h-full bg-dark-600 flex-col items-center justify-center">
                          <PhotoIcon className="h-8 w-8 text-dark-400 mb-2" />
                          <p className="text-xs text-dark-400 text-center">Failed to load</p>
                        </div>
                      </div>
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <MagnifyingGlassPlusIcon className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tags */}
          {task.tags && task.tags.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-white mb-3">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {task.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-dark-700 text-dark-300 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Completion Notes */}
          {task.completionNotes && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-white mb-2">Completion Notes</h3>
              <p className="text-dark-300">{task.completionNotes}</p>
            </div>
          )}

          {/* Action Buttons */}
          {canManageTask && task.status !== 'completed' && (
            <div className="flex items-center space-x-4 pt-6 border-t border-dark-700">
              {task.status === 'pending' && (
                <button
                  onClick={() => handleTaskAction('start')}
                  disabled={actionLoading}
                  className="btn btn-primary"
                >
                  <PlayIcon className="h-4 w-4 mr-2" />
                  Start Task
                </button>
              )}
              
              {task.status === 'in-progress' && (
                <>
                  <button
                    onClick={() => handleTaskAction('pause')}
                    disabled={actionLoading}
                    className="btn btn-secondary"
                  >
                    <PauseIcon className="h-4 w-4 mr-2" />
                    Pause Task
                  </button>
                  <button
                    onClick={() => setShowCompletionModal(true)}
                    disabled={actionLoading}
                    className="btn btn-success"
                  >
                    <CheckIcon className="h-4 w-4 mr-2" />
                    Complete Task
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Image Modal */}
      {showImageModal && selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-7xl max-h-full">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
            >
              <XMarkIcon className="h-8 w-8" />
            </button>
            <img
              src={selectedImage}
              alt="Task attachment"
              className="max-w-full max-h-full object-contain"
            />
          </div>
        </div>
      )}

      {/* Completion Modal */}
      {showCompletionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-white mb-4">Complete Task</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-white mb-2">
                Completion Notes (Optional)
              </label>
              <textarea
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
                className="form-input w-full"
                rows={4}
                placeholder="Add any notes about the task completion..."
              />
            </div>
            <div className="flex items-center justify-end space-x-4">
              <button
                onClick={() => {
                  setShowCompletionModal(false);
                  setCompletionNotes('');
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => handleTaskAction('complete')}
                disabled={actionLoading}
                className="btn btn-success"
              >
                {actionLoading ? 'Completing...' : 'Complete Task'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskDetails;